import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import "@/App.css";
import NuvolaLanding from "@/components/NuvolaLanding";
import NuvolaGame from "@/components/NuvolaGame";
import { Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function App() {
  // 'landing' | 'transitioning' | 'game'
  const [stage, setStage] = useState("landing");
  const [email, setEmail] = useState("");
  const [audioOn, setAudioOn] = useState(false);
  const audioRef = useRef(null);

  const startAmbient = useCallback(() => {
    if (audioRef.current) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      // Pink-ish noise for wind
      const bufferSize = 2 * ctx.sampleRate;
      const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      let b0 = 0, b1 = 0, b2 = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99765 * b0 + white * 0.0990460;
        b1 = 0.96300 * b1 + white * 0.2965164;
        b2 = 0.57000 * b2 + white * 1.0526913;
        output[i] = (b0 + b1 + b2 + white * 0.1848) * 0.08;
      }
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuffer;
      noise.loop = true;

      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 520;
      filter.Q.value = 0.7;

      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      lfo.frequency.value = 0.08;
      lfoGain.gain.value = 200;
      lfo.connect(lfoGain).connect(filter.frequency);
      lfo.start();

      const gain = ctx.createGain();
      gain.gain.value = 0.0;

      noise.connect(filter).connect(gain).connect(ctx.destination);
      noise.start();

      // fade in
      gain.gain.linearRampToValueAtTime(0.55, ctx.currentTime + 1.4);

      audioRef.current = { ctx, gain, noise };
    } catch (e) {
      // ignore
    }
  }, []);

  const stopAmbient = useCallback(() => {
    if (!audioRef.current) return;
    const { ctx, gain } = audioRef.current;
    try {
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.4);
      setTimeout(() => {
        try { ctx.close(); } catch (_) { /* noop */ }
      }, 500);
    } catch (_) { /* noop */ }
    audioRef.current = null;
  }, []);

  const toggleAudio = useCallback(() => {
    setAudioOn((v) => {
      const next = !v;
      if (next) startAmbient(); else stopAmbient();
      return next;
    });
  }, [startAmbient, stopAmbient]);

  useEffect(() => {
    return () => stopAmbient();
  }, [stopAmbient]);

  const handleSubmitEmail = useCallback(async (value) => {
    setEmail(value);
    setStage("transitioning");
    try {
      await axios.post(`${API}/subscribe`, { email: value });
    } catch (e) {
      // Even if backend fails, continue the experience
      console.warn("subscribe failed", e?.message);
    }
    // Slight delay for cloud pulse → dissolve, then game
    setTimeout(() => setStage("game"), 1800);
  }, []);

  const handleExitToLanding = useCallback(() => {
    // Scroll back to the top so the intro plays from frame 0 again.
    window.scrollTo(0, 0);
    setStage("landing");
  }, []);

  return (
    <div className="App" data-testid="nuvola-app">
      {/* persistent atmosphere */}
      <div className="grain" />
      <div className="vignette" />

      {stage !== "game" && (
        <NuvolaLanding
          onSubmitEmail={handleSubmitEmail}
          stage={stage}
          audioOn={audioOn}
          toggleAudio={toggleAudio}
        />
      )}

      {stage === "game" && (
        <NuvolaGame
          email={email}
          audioOn={audioOn}
          toggleAudio={toggleAudio}
          apiBase={API}
          onExitToLanding={handleExitToLanding}
        />
      )}

      <Toaster theme="dark" position="bottom-center" />
    </div>
  );
}
